import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import {
	compactDateSlotProps,
	primaryButtonSx,
	SectionCard,
} from "../sharedStyles";

function PaymentWorkingDashboard() {
	const { setSnackBar, setSnackBarOpen } = GlobalState();
	const [fromDate, setFromDate] = useState(dayjs().subtract(30, "day"));
	const [toDate, setToDate] = useState(dayjs());
	const [downloading, setDownloading] = useState(false);
	const [error, setError] = useState("");

	const rangeError = useMemo(() => {
		if (!fromDate?.isValid() || !toDate?.isValid()) {
			return "Select both from and to dates.";
		}
		if (toDate.isBefore(fromDate, "day")) {
			return "To date cannot be before from date.";
		}
		return "";
	}, [fromDate, toDate]);

	const canDownload = !rangeError && !downloading;

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({
			...prev,
			message,
			severity,
		}));
	};

	const handleDownload = async () => {
		if (rangeError) {
			setError(rangeError);
			return;
		}

		try {
			setDownloading(true);
			setError("");

			const startDate = fromDate.format("YYYY-MM-DD");
			const endDate = toDate.format("YYYY-MM-DD");
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/payment-working/download",
				{
					params: {
						start_date: startDate,
						end_date: endDate,
					},
					responseType: "blob",
				}
			);

			const contentType = res.headers?.["content-type"] || "";
			if (contentType.includes("application/json")) {
				const text = await res.data.text();
				const errorData = JSON.parse(text);
				throw new Error(errorData.message || "Failed to download file");
			}

			const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute(
				"download",
				`mbo_payment_working_${startDate}_to_${endDate}.csv`
			);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			showSnack("Payment working file downloaded", "success");
		} catch (downloadError) {
			let message = "Failed to download payment working file";
			const data = downloadError?.response?.data;
			if (data instanceof Blob) {
				try {
					const text = await data.text();
					const parsed = JSON.parse(text);
					message = parsed.message || message;
				} catch (_parseError) {
					message = downloadError?.message || message;
				}
			} else if (data?.message) {
				message = data.message;
			} else if (downloadError?.message) {
				message = downloadError.message;
			}
			setError(message);
			showSnack(message, "error");
		} finally {
			setDownloading(false);
		}
	};

	return (
		<Stack spacing={3}>
			<SectionCard title="Download by date range">
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack
						direction="row"
						flexWrap="wrap"
						useFlexGap
						spacing={1.5}
						alignItems="center"
					>
						<DatePicker
							label="From"
							format="DD/MM/YY"
							value={fromDate}
							maxDate={dayjs()}
							onChange={(value) => {
								const nextFrom = value || null;
								setFromDate(nextFrom);
								setToDate((prevTo) =>
									prevTo?.isValid() &&
									nextFrom?.isValid() &&
									prevTo.isBefore(nextFrom, "day")
										? nextFrom
										: prevTo
								);
								setError("");
							}}
							slotProps={compactDateSlotProps}
						/>
						<DatePicker
							label="To"
							format="DD/MM/YY"
							value={toDate}
							minDate={fromDate?.isValid() ? fromDate : undefined}
							maxDate={dayjs()}
							disabled={!fromDate?.isValid()}
							onChange={(value) => {
								setToDate(value || null);
								setError("");
							}}
							slotProps={compactDateSlotProps}
						/>
						<Button
							variant="contained"
							size="small"
							startIcon={<DownloadOutlinedIcon />}
							onClick={handleDownload}
							disabled={!canDownload}
							sx={{
								...primaryButtonSx,
								height: 36,
								px: 2,
							}}
						>
							{downloading ? "Downloading..." : "Download CSV"}
						</Button>
					</Stack>
				</LocalizationProvider>

				{(error || rangeError) && (
					<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
						{error || rangeError}
					</Alert>
				)}
			</SectionCard>

			<Box
				sx={{
					p: 2,
					borderRadius: 2.5,
					border: "1px solid rgba(2, 136, 209, 0.18)",
					bgcolor: "rgba(2, 136, 209, 0.06)",
				}}
			>
				<Typography variant="body2" color="text.secondary">
					Files are generated from the MBO Payment Working table using bill date.
					If no data is available, upload sale data and apply offers first.
				</Typography>
			</Box>
		</Stack>
	);
}

export default PaymentWorkingDashboard;
